import { memo, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { copyObject } from 'common/copyObject';
import { useStore } from 'store/StoreContext';
import { storeOnetimePasswordGenerator } from 'store/register-user/RegisterUserData';
import { callCheckOnetimePasswordApi, CheckOnetimePasswordResData } from 'requests/RequestUser';
import { RoutePath } from 'consts/RoutePath';
import { CircularProgress } from '@material-ui/core';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';
import { ApiError } from 'requests/RequestBase';
import { ApiErrorCode, findErrorMessageByApiError } from 'consts/ApiErrorCode';

type Props = {};

export const UserRegister2Template = memo((_props: Props) => {
	const history = useHistory();
	const [store, dispatch] = useStore();
	const [registerUserData, setRegisterUserData] = useState(copyObject(store.registerUserData));
	const [formData, setFormData] = useState({ onetime_password: '' });
	const [formErrors, setFormErrors] = useState({ onetime_password: '' });
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [processLoading, setProcessLoading] = useState(false);

	// ワンタイムパスワード入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, onetime_password: event.target.value }));
	};

	// 確認ボタンのonClickで呼ぶ
	const checkOnetimePassword = async () => {
		setProcessLoading(true);

		// エラー解除
		setFormErrors((prev) => ({ ...prev, onetime_password: '' }));

		// ワンタイムパスワード検証
		if (!formData.onetime_password.trim()) {
			setFormErrors((prev) => ({ ...prev, onetime_password: ReactError.E0001 }));
			setProcessLoading(false);
			return;
		}
		const checkOnetimePasswordResult = await callCheckOnetimePasswordApi({
			mailaddress: registerUserData.mailaddress_1,
			onetimepass: formData.onetime_password,
		});
		if (checkOnetimePasswordResult.status !== HttpStatus.OK) {
			const error = checkOnetimePasswordResult.data as ApiError;
			if (error.error_code === ApiErrorCode.E1157.code) {
				setFormErrors((prev) => ({ ...prev, onetime_password: findErrorMessageByApiError(error) }));
				setProcessLoading(false);
				return;
			}
			history.push(RoutePath.ERROR);
			return;
		}
		const data = checkOnetimePasswordResult.data as CheckOnetimePasswordResData;
		if (!data.exist) {
			setFormErrors((prev) => ({ ...prev, onetime_password: 'ワンタイムパスワードが正しくありません。' }));
			setProcessLoading(false);
			return;
		}

		// ワンタイムパスワード確認をOKにする
		setRegisterUserData((prev) => ({ ...prev, isOnetimePasswordOk: true }));
		// ストアに格納する
		storeOnetimePasswordGenerator(dispatch, registerUserData);

		// 次のページへ
		history.push(RoutePath.USER_REGISTER_3_1);
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		if (!store.registerUserData.mailaddress_1) {
			console.error(`error: mailaddress not exists.`);
			history.push(RoutePath.ERROR);
		}
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenID新規登録｜仮パスワード入力';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors]);

	return (
		<main>
			<div className="section">
				<div className="section-in" ref={scrollRef}>
					<div className="steps">
						<img className="pc" src="images/img-steps-02.svg" alt="仮パスワード入力" />
						<img className="sp" src="images/img-steps-02_sp.svg" alt="" />
					</div>
					<div className="step-title sp">仮パスワード入力</div>
					<div className="provisional-text">
						<p>ご登録いただいたメールアドレスへメールをお送りしました。</p>
						<p className="bold">件名：【GakkenID】仮登録完了のご案内 </p>
						<p>
							2時間以内にメールに記載されたワンタイムパスワードを入力し、「確認」ボタンから
							<br />
							本会員登録をお願いいたします。
						</p>
					</div>

					{isFormError ? (
						<div className="error-box" ref={errorRef}>
							<>
								<p>※下記項目は全て必須となります。</p>
								<div className="error-box-in">
									{formErrors.onetime_password !== '' ? (
										<p>【ワンタイムパスワード】{formErrors.onetime_password}</p>
									) : null}
								</div>
							</>
						</div>
					) : null}

					<div className="form-box">
						<div className="form-head">
							<label htmlFor="onetime_password">ワンタイムパスワード</label>
						</div>
						<div className="form-input">
							<input
								id="onetime_password"
								type="password"
								name="onetime_password"
								value={formData.onetime_password}
								onChange={handleChangeValue}
							/>
						</div>
						<p className="error">{formErrors.onetime_password}</p>
					</div>

					<div className="btn-box">
						<div className="btn gr">
							{processLoading ? <CircularProgress /> : <button onClick={checkOnetimePassword}>確認</button>}
						</div>
					</div>
				</div>
			</div>
		</main>
	);
});
